import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PreseasonGameInvitationControllerService } from '../api/index.ts';
import { selectUserId, selectTeams } from '../store/userSlice';
import './PreseasonGameInvitationLanding.css';

function formatDate(d) {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return d;
}

function availabilityLabel(status) {
  switch (status) {
    case 'AVAILABLE': return null;
    case 'UNAVAILABLE_DELETED': return 'Termin został usunięty przez organizatora';
    case 'UNAVAILABLE_QUOTA_FULL': return 'Wszystkie miejsca w tym terminie są już zajęte';
    default: return null;
  }
}

function PreseasonGameInvitationLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useSelector(selectUserId);
  const teamsFromStore = useSelector(selectTeams);
  const teams = useMemo(() => teamsFromStore || [], [teamsFromStore]);

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [respondingTeamId, setRespondingTeamId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const fetchInvitation = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await PreseasonGameInvitationControllerService.getPublic(token);
      setInvitation(data);
      setSelectedIds(prev => prev.filter(id => data.terms.some(t => t.invitationTermId === id && t.availability === 'AVAILABLE')));
    } catch (err) {
      console.error('Błąd pobierania zaproszenia', err);
      setLoadError(err?.status === 404
        ? 'Zaproszenie nie istnieje lub wygasło.'
        : 'Nie udało się pobrać zaproszenia. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvitation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (userId && teams.length > 0 && !respondingTeamId) {
      setRespondingTeamId(teams[0].id);
    }
  }, [userId, teams, respondingTeamId]);

  const isLoggedIn = !!userId;
  const fromPath = location.pathname;

  const toggleTerm = (termId) => {
    setSelectedIds(prev => prev.includes(termId)
      ? prev.filter(id => id !== termId)
      : [...prev, termId]);
  };

  const handleSubmit = async () => {
    if (!respondingTeamId) {
      toast.error('Wybierz drużynę, w imieniu której odpowiadasz');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Zaznacz przynajmniej jeden termin');
      return;
    }
    setSubmitting(true);
    try {
      const result = await PreseasonGameInvitationControllerService.respond(token, {
        respondingTeamId,
        selectedInvitationTermIds: selectedIds,
        message: message.trim() || undefined
      });
      toast.success(`Wysłano ${result.createdCount} ${result.createdCount === 1 ? 'zapytanie' : 'zapytania'}`);
      navigate('/preseason-games-plans');
    } catch (err) {
      console.error('Błąd wysyłania', err);
      if (err?.status === 409) {
        const msg = err?.body?.message || '';
        if (msg.includes('już odpowiedziała')) {
          toast.error('Twoja drużyna już odpowiedziała na to zaproszenie');
        } else if (msg.includes('niedostępne')) {
          toast.error('Niektóre wybrane terminy są już niedostępne. Odśwież listę i spróbuj ponownie.');
          await fetchInvitation();
        } else if (msg.includes('dezaktywowane')) {
          toast.error('Zaproszenie zostało dezaktywowane przez organizatora');
        } else {
          toast.error(msg || 'Konflikt — odśwież widok i spróbuj ponownie');
          await fetchInvitation();
        }
      } else {
        toast.error('Wystąpił błąd. Spróbuj ponownie później.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="invitation-landing-container">
        <div className="invitation-landing-card">
          <div className="invitation-landing-content" style={{ textAlign: 'center', color: '#666' }}>
            Ładowanie zaproszenia...
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="invitation-landing-container">
        <div className="invitation-landing-card">
          <div className="invitation-landing-header">
            <div className="invitation-landing-header-text">
              <h1>Zaproszenie do sparingu</h1>
            </div>
          </div>
          <div className="invitation-landing-content">
            <div className="invitation-error-box">{loadError}</div>
            <div style={{ textAlign: 'center' }}>
              <Link to="/" className="btn btn-primary">Wróć do strony głównej</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) return null;

  const availableCount = invitation.terms.filter(t => t.availability === 'AVAILABLE').length;

  const header = (
    <div className="invitation-landing-header">
      {invitation.logoUrl && (
        <img
          className="invitation-landing-logo"
          src={invitation.logoUrl}
          alt={invitation.sportsClubName || 'Logo klubu'}
        />
      )}
      <div className="invitation-landing-header-text">
        <h1>Zaproszenie do sparingu</h1>
        <p className="team-name">
          {invitation.teamName}{invitation.teamTier ? ` (${invitation.teamTier})` : ''}
        </p>
        {invitation.sportsClubName && (
          <p className="club-name">
            {invitation.sportsClubName}{invitation.sportsClubCity ? ` · ${invitation.sportsClubCity}` : ''}
          </p>
        )}
      </div>
    </div>
  );

  const termsSection = (
    <>
      <h3 className="invitation-section-title">
        <span>Proponowane terminy</span>
        <span className="invitation-section-hint">
          (możesz wybrać kilka terminów; ostatecznie organizator zaakceptuje tylko jeden z nich)
        </span>
      </h3>
      {invitation.terms.length === 0 ? (
        <p style={{ color: '#666', fontSize: '0.85rem' }}>Brak terminów w tym zaproszeniu.</p>
      ) : (
        <div className="invitation-terms-list">
          {invitation.terms.map(t => {
            const disabled = t.availability !== 'AVAILABLE' || !invitation.active || !isLoggedIn;
            const reason = availabilityLabel(t.availability);
            const checked = selectedIds.includes(t.invitationTermId);
            return (
              <label
                key={t.invitationTermId}
                className={[
                  'invitation-term-row',
                  disabled ? 'invitation-term-row--disabled' : '',
                  checked ? 'invitation-term-row--selected' : ''
                ].filter(Boolean).join(' ')}
              >
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => toggleTerm(t.invitationTermId)}
                />
                <span className="invitation-term-dates">
                  {formatDate(t.startDate)} – {formatDate(t.endDate)}
                </span>
                {reason && <span className="invitation-term-reason">{reason}</span>}
              </label>
            );
          })}
        </div>
      )}
    </>
  );

  if (!isLoggedIn) {
    return (
      <div className="invitation-landing-container">
        <div className="invitation-landing-card">
          {header}
          <div className="invitation-landing-content">
            {!invitation.active && (
              <div className="invitation-disabled-banner">
                To zaproszenie zostało dezaktywowane przez organizatora.
              </div>
            )}
            {termsSection}
            <div className="invitation-auth-prompt">
              <h3>Czy masz konto w aplikacji?</h3>
              <p>Aby odpowiedzieć na to zaproszenie, musisz być zalogowany jako klub sportowy.</p>
              <div className="invitation-auth-prompt-buttons">
                <Link to={`/login?returnTo=${encodeURIComponent(fromPath)}`} className="btn btn-primary">
                  Tak, mam konto — Zaloguj się
                </Link>
                <Link to={`/register?returnTo=${encodeURIComponent(fromPath)}`} className="btn btn-secondary">
                  Nie, zarejestruj klub sportowy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-landing-container">
      <div className="invitation-landing-card">
        {header}
        <div className="invitation-landing-content">
          {!invitation.active && (
            <div className="invitation-disabled-banner">
              To zaproszenie zostało dezaktywowane przez organizatora.
            </div>
          )}
          {termsSection}

          {availableCount === 0 ? (
            <div className="invitation-error-box">Wszystkie terminy są obecnie niedostępne.</div>
          ) : (
            <>
              <div className="invitation-info-banner">
                Zaznacz terminy, które Cię interesują. Możesz wybrać <strong>więcej niż jeden</strong> — organizator zobaczy wszystkie i wybierze ostatecznie <strong>jeden</strong>, akceptując odpowiedź na ten konkretny termin (pozostałe Twoje propozycje z tego linku zostaną wtedy automatycznie odrzucone).
              </div>

              {teams.length > 1 && (
                <div className="invitation-form-group">
                  <label htmlFor="responding-team">Odpowiadasz w imieniu drużyny:</label>
                  <select
                    id="responding-team"
                    value={respondingTeamId || ''}
                    onChange={(e) => setRespondingTeamId(Number(e.target.value))}
                  >
                    {teams.map(t => (
                      <option key={t.id} value={t.id}>{t.teamName}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="invitation-form-group">
                <label htmlFor="invitation-message">Wiadomość do organizatora (opcjonalnie):</label>
                <textarea
                  id="invitation-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Np. preferowana godzina, dostępność boiska..."
                />
              </div>

              <div className="invitation-submit-row">
                <button
                  className="invitation-submit-btn"
                  onClick={handleSubmit}
                  disabled={submitting || selectedIds.length === 0 || !invitation.active}
                >
                  {submitting ? 'Wysyłanie...' : 'Wyślij zapytanie'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PreseasonGameInvitationLanding;
